import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import CoreInput from "../components/ui/core/CoreInput";
import StateBadge from "../components/tickets/StateBadge";
import ArticleCard from "../components/tickets/ArticleCard";
import { linkedFilesFor } from "../components/tickets/ticketDisplay";
import { useTicketStore } from "../hooks/useTicketStore";
import type { IArticle } from "../types/tickets";

const PRIORITY_COLORS: Record<
  number,
  "error" | "warning" | "info" | "default"
> = {
  1: "error",
  2: "warning",
  3: "info",
};

const TicketDetailPage: React.FC = observer(() => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const ticketStore = useTicketStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const [replyBody, setReplyBody] = useState("");

  useEffect(() => {
    ticketStore.setLiveViewing(true);
    return () => ticketStore.setLiveViewing(false);
  }, []);

  useEffect(() => {
    if (ticketId) {
      ticketStore.loadTicket(Number(ticketId));
    }
  }, [ticketId]);

  const ticket = ticketStore.activeTicket;

  const handleSubmitReply = async () => {
    if (!replyBody.trim() || !ticket) return;
    const response = await ticketStore.createReply(ticket.id, replyBody);
    if (response?.success) {
      setReplyBody("");
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (ticketStore.isDetailLoading) {
    return (
      <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="text" width={400} height={30} sx={{ mt: 1 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (ticketStore.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<span>&larr;</span>}
          onClick={() => navigate("/tickets")}
        >
          Back to Tickets
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {ticketStore.error}
        </Typography>
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<span>&larr;</span>}
          onClick={() => navigate("/tickets")}
        >
          Back to Tickets
        </Button>
        <Typography sx={{ mt: 2, color: theme.palette.text.secondary }}>
          Ticket not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
      <Button
        startIcon={<span>&larr;</span>}
        onClick={() => navigate("/tickets")}
        sx={{ mb: 2 }}
      >
        Back to Tickets
      </Button>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            #{ticket.number}
          </Typography>
          <StateBadge stateId={ticket.state_id} label={ticket.state} />
          <Chip
            label={ticket.priority || "Normal"}
            color={PRIORITY_COLORS[ticket.priority_id] || "default"}
            size="small"
            variant="outlined"
          />
        </Box>
        <Typography variant="h6">{ticket.title}</Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
        >
          Created {formatDateTime(ticket.created_at)} &middot; Updated{" "}
          {formatDateTime(ticket.updated_at)}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {ticket.articles.map((article: IArticle) => (
        <ArticleCard
          key={article.id}
          article={article}
          ticketId={ticket.id}
          linkedFiles={linkedFilesFor(ticket.files, article.id)}
          formatDateTime={formatDateTime}
        />
      ))}

      <Box sx={{ mt: 3 }}>
        <CoreInput
          placeholder="Type your reply..."
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          disabled={ticketStore.replyLoading}
          sx={{
            mb: 1.5,
          }}
          label="Reply"
        />
        <Button
          variant="contained"
          onClick={handleSubmitReply}
          disabled={!replyBody.trim() || ticketStore.replyLoading}
          startIcon={
            ticketStore.replyLoading ? <CircularProgress size={16} /> : null
          }
        >
          {ticketStore.replyLoading ? "Sending..." : "Send Reply"}
        </Button>
      </Box>
    </Box>
  );
});

export default TicketDetailPage;
